/** Mapeo DocumentRecord (GraphQL) → ficha SPEC-12 (client-side fallback). */

import { extractVatLines, parseAmount, resolveAiSummary } from './fichaAmounts';
import {
  canonicalizeBedrockInvoice,
  getBedrockConfidence,
  getBedrockField,
  resolveBedrockMetadata,
} from './bedrockNormalize';
import { formatComprobanteLabel, formatVoucherTitle } from './fiscalComprobante';
import { ENRICHMENT_SOURCE_COPY } from '../constants/fiscalProfile';

const ENRICHMENT_FIELD_MAP = {
  cuit_emisor: 'parties.issuer.cuit',
  razon_social_emisor: 'parties.issuer.name',
  cuit_receptor: 'parties.receiver.cuit',
  razon_social_receptor: 'parties.receiver.name',
};

function normalizeCuit(value) {
  return String(value || '').replace(/\D/g, '');
}

function confidenceBand(score) {
  if (score >= 0.85) return 'high';
  if (score >= 0.6) return 'medium';
  return 'low';
}

const FICHA_CONFIDENCE_KEYS = {
  'voucher.full_number': ['punto_de_venta', 'numero_documento'],
  'voucher.issue_date': ['fecha_emision'],
  'parties.issuer.name': ['razon_social_emisor'],
  'parties.issuer.cuit': ['cuit_emisor'],
  'parties.receiver.name': ['razon_social_receptor'],
  'parties.receiver.cuit': ['cuit_receptor'],
  'fiscal.cae': ['CAE'],
  'fiscal.cae_due': ['cae_vencimiento', 'fecha_vencimiento_cae'],
  'amounts.total': ['monto_total'],
  'amounts.net_taxed': ['subtotal'],
};

function bedrockConfidence(fieldKey, bedrock) {
  const keys = FICHA_CONFIDENCE_KEYS[fieldKey] || [];
  const scores = keys
    .map((k) => getBedrockConfidence(bedrock, k))
    .filter((s) => s != null);
  if (!scores.length) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

function fieldConfidence(fieldKey, value, bedrock, qrData) {
  if (value == null || value === '') {
    const bs = bedrockConfidence(fieldKey, bedrock);
    return bs != null ? bs : 0.35;
  }

  const bedrockScore = bedrockConfidence(fieldKey, bedrock);
  if (bedrockScore != null && !qrData) return bedrockScore;

  const qrMap = {
    'parties.issuer.cuit': ['cuit_emisor', 'cuit'],
    'amounts.total': ['monto_total', 'importe'],
    'voucher.issue_date': ['fecha_emision', 'fecha'],
    'fiscal.cae': ['CAE', 'codAut'],
  };
  const mapping = qrMap[fieldKey];
  if (!mapping || !qrData) {
    if (bedrockScore != null) return bedrockScore;
    return 0.75;
  }
  const [extKey, qrKey] = mapping;
  const ext = bedrock?.[extKey];
  const reg = qrData?.[qrKey];
  if (ext == null || reg == null) return 0.55;
  const a = String(ext).replace(/[-.]/g, '');
  const b = String(reg).replace(/[-.]/g, '');
  return a === b ? 0.95 : 0.45;
}

const BBOX_HINTS = {
  'voucher.full_number': { page: 1, x: 0.62, y: 0.08, w: 0.18, h: 0.03 },
  'voucher.issue_date': { page: 1, x: 0.55, y: 0.12, w: 0.15, h: 0.03 },
  'parties.issuer.cuit': { page: 1, x: 0.08, y: 0.18, w: 0.25, h: 0.03 },
  'parties.issuer.name': { page: 1, x: 0.08, y: 0.14, w: 0.4, h: 0.04 },
  'amounts.total': { page: 1, x: 0.7, y: 0.74, w: 0.2, h: 0.03 },
  'fiscal.cae': { page: 1, x: 0.55, y: 0.82, w: 0.25, h: 0.03 },
  'fiscal.cae_due': { page: 1, x: 0.55, y: 0.86, w: 0.20, h: 0.03 },
};

function formatPosNumber(pos, number) {
  const posS = String(pos || '0').padStart(4, '0');
  const numS = String(number || '0').padStart(8, '0');
  return `${posS}-${numS}`;
}

function deriveArcaState(bedrock, qrData) {
  if (!qrData) return { state: 'sin_constatar', observaciones: [], checked_at: null };
  const checks = [
    normalizeCuit(bedrock?.cuit_emisor) === normalizeCuit(qrData.cuit),
    Math.abs(parseFloat(bedrock?.monto_total || 0) - parseFloat(qrData.importe || 0)) < 0.02,
    String(bedrock?.CAE) === String(qrData.codAut),
  ];
  const failed = checks.filter((ok) => !ok).length;
  if (failed === 0) return { state: 'aprobado', observaciones: [], checked_at: new Date().toISOString() };
  if (failed < checks.length) {
    return { state: 'observado', observaciones: ['Diferencias con datos del QR'], checked_at: new Date().toISOString() };
  }
  return { state: 'rechazado', observaciones: ['No coincide con QR'], checked_at: new Date().toISOString() };
}

function enrichmentSourcesByField(record = {}) {
  const corrections = record?.fiscal_enrichment?.corrections || [];
  const map = {};
  corrections.forEach((row) => {
    const fichaKey = ENRICHMENT_FIELD_MAP[row.field];
    if (fichaKey) map[fichaKey] = row.source;
  });
  return map;
}

function parseFieldEdits(record = {}) {
  const raw = record?.field_edits;
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
}

function applyFieldEdits(fields, fieldEdits = {}) {
  const next = { ...fields };
  Object.entries(fieldEdits).forEach(([key, edit]) => {
    if (!next[key]) return;
    next[key] = {
      ...next[key],
      value: edit?.value ?? next[key].value,
      edited: Boolean(edit?.edited),
      edited_by: edit?.edited_by,
      edited_at: edit?.edited_at,
      original_value: edit?.original_value,
    };
  });
  return next;
}

export function fieldEditsFromFicha(ficha) {
  const edits = {};
  Object.entries(ficha?.fields || {}).forEach(([key, field]) => {
    if (!field?.edited) return;
    edits[key] = {
      value: field.value,
      edited: true,
      original_value: field.original_value,
      edited_by: field.edited_by,
      edited_at: field.edited_at,
    };
  });
  return edits;
}

function normalizeArcaValidation(record, bedrock, qrData) {
  const persisted = record?.arca_validation;
  if (persisted?.status) {
    return {
      ...persisted,
      state: persisted.status,
      observaciones: persisted.observations || persisted.observaciones || [],
      checked_at: persisted.checked_at,
    };
  }
  return deriveArcaState(bedrock, qrData);
}

export function mapDocumentToFicha(record, { canEdit = false } = {}) {
  if (!record) return null;
  const bedrock = canonicalizeBedrockInvoice(resolveBedrockMetadata(record));
  const qrData = record.qr_data;
  const pos = getBedrockField(bedrock, 'punto_de_venta');
  const number = getBedrockField(bedrock, 'numero_documento');
  const fullNumber = formatPosNumber(pos, number);
  const caeDue =
    getBedrockField(bedrock, 'cae_vencimiento') ||
    getBedrockField(bedrock, 'fecha_vencimiento_cae');
  const net = parseAmount(bedrock.subtotal);
  const iva21 = parseAmount(getBedrockField(bedrock, 'iva_21'));
  const iva105 = parseAmount(getBedrockField(bedrock, 'iva_10_5'));
  const vatLines = extractVatLines(bedrock);
  const vatSum = vatLines.reduce((s, row) => s + row.amount, 0);
  const total =
    parseAmount(bedrock.monto_total) || net + vatSum || net + iva21 + iva105;
  const fieldEdits = parseFieldEdits(record);

  const amounts = {
    currency: bedrock.moneda || 'ARS',
    exchange_rate: qrData?.ctz || null,
    net_taxed: net,
    net_untaxed: 0,
    vat: vatLines,
    perceptions: (bedrock.retenciones || []).map((r) => ({
      label: r.nombre || 'IIBB',
      amount: parseFloat(r.importe) || 0,
    })),
    other_taxes: parseAmount(bedrock.impuesto) && !vatLines.length
      ? parseAmount(bedrock.impuesto)
      : 0,
    total,
  };
  if (fieldEdits['amounts.total']?.value != null) {
    amounts.total = parseAmount(fieldEdits['amounts.total'].value);
  }

  const rawFields = {
    'voucher.full_number': fullNumber,
    'voucher.issue_date': bedrock.fecha_emision,
    'parties.issuer.name': bedrock.razon_social_emisor,
    'parties.issuer.cuit': bedrock.cuit_emisor,
    'parties.receiver.name': bedrock.razon_social_receptor,
    'parties.receiver.cuit': bedrock.cuit_receptor,
    'fiscal.cae': bedrock.CAE,
    'fiscal.cae_due': caeDue,
    'amounts.total': amounts.total,
    'amounts.net_taxed': net,
  };

  const enrichmentSources = enrichmentSourcesByField(record);

  const fields = {};
  Object.entries(rawFields).forEach(([key, value]) => {
    const conf = fieldConfidence(key, value, bedrock, qrData);
    const enrichmentSource = enrichmentSources[key];
    fields[key] = {
      value,
      confidence: conf,
      band: confidenceBand(conf),
      bbox: BBOX_HINTS[key] || null,
      edited: false,
      enrichmentSource,
      enrichmentLabel: enrichmentSource ? ENRICHMENT_SOURCE_COPY[enrichmentSource] : null,
    };
  });

  const mergedFields = applyFieldEdits(fields, fieldEdits);

  const docType = (record.doc_type || '').toLowerCase();
  const status = docType === 'unclassified' ? 'unclassified' : 'validated';

  return {
    doc_id: record.document_id,
    pipeline_status: record.status || null,
    reprocess_in_progress: Boolean(record.reprocess_in_progress),
    hidden_reason: record.hidden_reason || null,
    doc_type: record.doc_type,
    subtype: `${(bedrock.tipo_documento || 'factura').toLowerCase()}_${(bedrock.letra_documento || 'a').toLowerCase()}`,
    letter: (bedrock.letra_documento || 'A').toUpperCase(),
    voucher_type_label: formatComprobanteLabel(bedrock),
    voucher_title: formatVoucherTitle(bedrock, bedrock.letra_documento),
    status,
    filename: record.filename,
    parties: {
      issuer: {
        name: mergedFields['parties.issuer.name']?.value ?? bedrock.razon_social_emisor,
        cuit: mergedFields['parties.issuer.cuit']?.value ?? bedrock.cuit_emisor,
        iva_condition: null,
      },
      receiver: {
        name: mergedFields['parties.receiver.name']?.value ?? bedrock.razon_social_receptor,
        cuit: mergedFields['parties.receiver.cuit']?.value ?? bedrock.cuit_receptor,
        iva_condition: null,
      },
    },
    voucher: {
      pos: String(pos || ''),
      number: String(number || ''),
      full_number: mergedFields['voucher.full_number']?.value ?? fullNumber,
      issue_date: mergedFields['voucher.issue_date']?.value ?? bedrock.fecha_emision,
      due_date: bedrock.fecha_vencimiento,
      payment_terms: bedrock.condiciones_de_venta,
    },
    amounts,
    fiscal: {
      cae: mergedFields['fiscal.cae']?.value ?? bedrock.CAE,
      cae_due: mergedFields['fiscal.cae_due']?.value ?? caeDue,
    },
    arca: normalizeArcaValidation(record, bedrock, qrData),
    fiscal_enrichment: record.fiscal_enrichment || null,
    accounting: {
      account: null,
      account_code: null,
      category: bedrock.categoria_documento,
    },
    ai_summary: resolveAiSummary(bedrock),
    items: bedrock.items || [],
    classifier_hypothesis: {
      type: bedrock.categoria_documento || bedrock.tipo_documento,
      confidence: 0.72,
    },
    fields: mergedFields,
    document: {
      key: record.key,
      mimetype: record.mimetype,
      bucket: record.bucket,
      pages: 1,
    },
    permissions: { can_edit: canEdit },
    raw: {
      bedrock_json: resolveBedrockMetadata(record),
      qr_data: qrData,
      qr_link: record.qr_link,
    },
  };
}

export function mergeFichaField(ficha, fieldKey, patch) {
  if (!ficha?.fields?.[fieldKey]) return ficha;
  return {
    ...ficha,
    fields: {
      ...ficha.fields,
      [fieldKey]: { ...ficha.fields[fieldKey], ...patch },
    },
  };
}

export function formatArs(amount) {
  const n = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  return `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
